// دستگاه های اجرایی و مسئولان آن‌ها
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/treasury${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = () => ({
  code: '', cms_code: '', title: '', brief_name: '',
  national_code: '', pay_id_no: '', city_id: 0, city: '',
});

/* جستجوی انتخابی — یک‌دست با سایر فرم‌های برنامه */
function Lookup({ label, value, endpoint, onPick, flex = 1 }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}?q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => search(q)} onChange={e => search(e.target.value)}
        placeholder="جستجو..." style={ui.input} />
      {value && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* مودال مسئولان واحد اجرایی */
function ManagersModal({ dep, onClose, onChanged }) {
  // دکمه‌های فرم FRMDEPARTMENTSMANAGER؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMDEPARTMENTSMANAGER', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const [rows, setRows]   = useState([]);
  const [sel, setSel]     = useState(null);
  const [mode, setMode]   = useState(null);          // 'new' | 'edit'
  const [form, setForm]   = useState({ people_id: 0, manager: '', event_date: '' });
  const [err, setErr]     = useState('');
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    const j = await API(`/departments/${dep.id}/managers/`);
    if (j.ok) { setRows(j.data); setSel(null); } else setErr(j.msg);
  }, [dep.id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr('');
    const body = JSON.stringify(form);
    const j = mode === 'new'
      ? await API(`/departments/${dep.id}/managers/create/`, { method: 'POST', body })
      : await API(`/departments/${dep.id}/managers/${sel.id}/update/`, { method: 'PUT', body });
    setBusy(false);
    if (j.ok) { setMode(null); load(); onChanged?.(); } else setErr(j.msg);
  };

  const del = async () => {
    if (!sel) return;
    setBusy(true); setErr('');
    const j = await API(`/departments/${dep.id}/managers/${sel.id}/delete/`, { method: 'DELETE' });
    setBusy(false);
    if (j.ok) { load(); onChanged?.(); } else setErr(j.msg);
  };

  return (
    <div style={ui.overlay} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>👤 مسئولان واحد اجرایی — {dep.title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={ui.mBody}>
          {err && <div style={ui.errBox}>⚠️ {err}</div>}
          <div style={{ ...ui.toolBtns, marginBottom: '8px' }}>
            {vbf.can('BTNNEW') && <button onClick={() => { setForm({ people_id: 0, manager: '', event_date: '' }); setErr(''); setMode('new'); }} style={ui.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={() => { if (!sel) return; setForm({ people_id: sel.people_id, manager: sel.manager, event_date: sel.event_date }); setErr(''); setMode('edit'); }}
              disabled={!sel} style={{ ...ui.btnEdit, opacity: sel ? 1 : 0.4 }}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={del} disabled={!sel || busy} style={{ ...ui.btnDel, opacity: sel ? 1 : 0.4 }}>✕ حذف</button>}
          </div>

          {mode && (
            <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px', marginBottom: '10px' }}>
              <div style={ui.row}>
                <Lookup label="مسئول" value={form.manager} endpoint="/departments/lookup/people/"
                  onPick={r => setForm(f => ({ ...f, people_id: r.id, manager: r.display }))} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>تاریخ</label>
                  <JalaliDatePicker value={form.event_date} onChange={v => setForm(f => ({ ...f, event_date: v }))} placeholder="تاریخ" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button onClick={() => { setMode(null); setErr(''); }} style={ui.btnCancel}>انصراف</button>
                {vbf.can('BTNOK') && <button onClick={save} disabled={busy} style={ui.btnSave}>{busy ? '⏳ ...' : '💾 ذخیره'}</button>}
              </div>
            </div>
          )}

          <table style={ui.table}>
            <thead><tr><th style={ui.th}>تاریخ</th><th style={ui.th}>مسئول</th></tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const s = sel?.id === r.id;
                return (
                  <tr key={r.id} onClick={() => setSel(s ? null : r)}
                    style={{ background: s ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf3e3', color: s ? '#fff' : '#111', cursor: 'pointer' }}>
                    <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: 'inherit' }}>{r.event_date || '•'}</td>
                    <td style={{ ...ui.td, textAlign: 'right', color: 'inherit' }}>{r.manager || '•'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <div style={ui.empty}>نام مسئول واحد اجرایی مشخص نشده است</div>}
        </div>
        <div style={ui.mFooter}>
          <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        </div>
      </div>
    </div>
  );
}

export default function DepartmentList({ user, onBack }) {
  // دکمه‌های فرم FRMDEPARTMENTS؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMDEPARTMENTS', ["BTNNEW","BTNEDIT","BTNDELETE","BTNDEPARTMENTSMANAGER","BTNOK"]);
  const [data, setData]         = useState([]);
  const pager = usePager();          // صفحه‌بندی مشترک — ۲۰۰ ردیف در هر صفحه
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const cols = ['کد', 'کد Cms', 'شهر', 'عنوان', 'نام اختصاری', 'شناسه ملی', 'شناسه واریز',
    'نام مسئول', 'تعداد طرح', 'تراکنش های طرح', 'تعداد سپرده'];
  const initW = [70, 80, 130, 260, 130, 120, 120, 170, 90, 120, 100];
  const [colW, setColW] = useState(initW);
  const colWRef = useRef(initW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async (opts = {}) => {
    setLoading(true); setError('');
    const p = new URLSearchParams({ search: opts.search ?? search, page: opts.page ?? 1 });
    const j = await API(`/departments/?${p.toString()}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setData(prev => pager.merge(prev, j, opts.page ?? 1));
    if ((opts.page ?? 1) === 1) setSelected(null);
  }, [search]);

  useEffect(() => { load({ page: 1 }); }, []);   // eslint-disable-line

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY()); setError(''); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({
      code: selected.code, cms_code: selected.cms_code, title: selected.title,
      brief_name: selected.brief_name, national_code: selected.national_code,
      pay_id_no: selected.pay_id_no, city_id: selected.city_id, city: selected.city,
    });
    setError(''); setModal('edit');
  };
  const openDel = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    setSaving(true); setError('');
    const body = JSON.stringify(form);
    const j = modal === 'new'
      ? await API('/departments/create/', { method: 'POST', body })
      : await API(`/departments/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); showToast(j.msg); load({ page: 1 }); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/departments/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); showToast('حذف شد'); load({ page: 1 }); }
    else { setError(j.msg); setModal(null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
        {vbf.can('BTNDEPARTMENTSMANAGER') && <button onClick={() => selected && setModal('managers')} disabled={!selected}
          style={{ ...ui.btnItems, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>👤 مسئولان</button>}
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load({ page: 1 })}
        style={{ ...ui.searchInput, flex: 1, minWidth: '180px' }} placeholder="جستجو (عنوان یا کد)..." />
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="دستگاه های اجرایی" subtitle="بخش اعتبارات خزانه — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}
        pager={pager} loading={loading} onMore={p => load({ page: p })}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((h, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf3e3';   // ردیف‌های یک‌درمیان کرم
              const tc  = sel ? '#fff' : '#111';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.code)}</td>
                  <td style={{ ...ui.td, color: tc }}>{toFa(r.cms_code) || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.city || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.title}>{r.title || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.brief_name || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{toFa(r.national_code) || '•'}</td>
                  <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{toFa(r.pay_id_no) || '•'}</td>
                  <td style={{ ...ui.td, textAlign: 'right', color: tc }}>{r.manager || '•'}</td>
                  <td style={{ ...ui.tdNum, color: tc }}>{fmtNum(r.program_count)}</td>
                  <td style={{ ...ui.tdNum, color: tc }}>{fmtNum(r.turnover_count)}</td>
                  <td style={{ ...ui.tdNum, color: tc }}>{fmtNum(r.deposit_count)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 دستگاه اجرایی یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ دستگاه اجرایی جدید' : '✎ ویرایش دستگاه اجرایی'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '130px' }}>
                  <label style={ui.mlabel}>کد</label>
                  <input type="number" value={form.code} onChange={e => F('code', e.target.value)}
                    placeholder="خودکار" style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '130px' }}>
                  <label style={ui.mlabel}>کد Cms</label>
                  <input type="number" value={form.cms_code} onChange={e => F('cms_code', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>عنوان</label>
                  <input value={form.title} onChange={e => F('title', e.target.value)} style={ui.input} />
                </div>
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>نام اختصاری</label>
                  <input value={form.brief_name} onChange={e => F('brief_name', e.target.value)} style={ui.input} />
                </div>
                <Lookup label="شهر" value={form.city} endpoint="/departments/lookup/city/"
                  onPick={r => setForm(f => ({ ...f, city_id: r.id, city: r.display }))} />
              </div>
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شناسه ملی</label>
                  <input value={form.national_code} onChange={e => F('national_code', e.target.value)} style={ui.input} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>شناسه واریز</label>
                  <input value={form.pay_id_no} onChange={e => F('pay_id_no', e.target.value)} style={ui.input} />
                </div>
              </div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'managers' && selected && (
        <ManagersModal dep={selected} onClose={() => setModal(null)} onChanged={() => load({ page: 1 })} />
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              <div style={{ fontSize: '34px', marginBottom: '10px' }}>🗑️</div>
              {toFa(selected?.code)} — {selected?.title}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
